import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid2 as Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  TextField
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import { AccountProgress, useAccounts, useRunAccount, useRunCatchUp, useRunNow, useSchedulerToggle, useSystemStatus } from "../api/hooks";
import { extractApiError } from "../api/client";
import { useNotificationStore } from "../store/notificationStore";
import { useSettingsStore } from "../store/settingsStore";
import { useAuthStore } from "../store/authStore";
import { StatusChip } from "../components/StatusChip";

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString();
}

function formatStageLabel(stage?: string | null) {
  if (!stage) return "";
  const map: Record<string, string> = {
    queued: "In coda",
    initializing: "Inizializzazione",
    prepare: "Preparazione",
    fetch: "Download messaggi",
    indexing: "Indicizzazione",
    archiving: "Compressione",
    summary: "Riepilogo",
    completed: "Completato"
  };
  return map[stage] ?? stage.replace(/_/g, " ");
}

function accountStatusLabel(status?: string | null) {
  switch ((status ?? "").toLowerCase()) {
    case "running":
      return "In corso";
    case "pending":
      return "In coda";
    case "completed":
      return "Completato";
    case "failed":
      return "Errore";
    default:
      return status ?? "Sconosciuto";
  }
}

function accountStatusColor(status?: string | null):
  | "default"
  | "primary"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "secondary" {
  switch ((status ?? "").toLowerCase()) {
    case "running":
      return "primary";
    case "pending":
      return "warning";
    case "completed":
      return "success";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function formatDuration(seconds?: number | null) {
  if (seconds == null || Number.isNaN(seconds)) {
    return "";
  }
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remaining = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remaining}s`;
  }
  return `${remaining}s`;
}

export function Dashboard() {
  const statusRefreshMs = useSettingsStore((state) => state.statusRefreshMs);
  const { data, isLoading, isRefetching, isError, error } = useSystemStatus(statusRefreshMs);
  const runNow = useRunNow();
  const runCatchUp = useRunCatchUp();
  const runAccount = useRunAccount();
  const accountsQuery = useAccounts();
  const [accountDates, setAccountDates] = useState<Record<string, string>>({});
  const toggleScheduler = useSchedulerToggle();
  const notify = useNotificationStore((state) => state.enqueue);
  const [runDate, setRunDate] = useState<string>("");
  const canManageSettings = useAuthStore((state) => state.canManageSettings());

  const schedulerRunning = data?.scheduler_running ?? false;
  const jobInProgress = data?.job_in_progress ?? false;
  const accountLabels = data?.account_labels ?? {};
  const accountsList = accountsQuery.data?.accounts ?? [];

  const statusLabel = useMemo(() => {
    if (jobInProgress) return "In esecuzione";
    return schedulerRunning ? "Schedulato" : "In pausa";
  }, [jobInProgress, schedulerRunning]);

  const lastRun = formatDate(data?.last_run_completed_iso ?? data?.last_run_started_iso);
  const nextRun = formatDate(data?.next_daily_run_iso ?? data?.next_run_iso);
  const sourceSyncEnabled = data?.source_sync_enabled ?? false;
  const sourceSyncIntervalHours = data?.source_sync_interval_hours ?? 6;
  const nextSourceSync = formatDate(data?.next_source_sync_iso);
  const schedulerModeLabel = sourceSyncEnabled
    ? (sourceSyncIntervalHours === 1 ? "Giornaliero + source-sync orario" : `Giornaliero + source-sync ${sourceSyncIntervalHours}h`)
    : "Solo giornaliero";
  const basePath = data?.base_path ?? "—";
  const progressEntries = useMemo(() => {
    if (!data?.accounts_progress) {
      return [] as Array<[string, AccountProgress]>;
    }
    const priority: Record<string, number> = {
      failed: 0,
      running: 1,
      pending: 2,
      completed: 3
    };
    return Object.entries(data.accounts_progress)
      .map(([account, snapshot]) => [account, snapshot as AccountProgress] as [string, AccountProgress])
      .sort((a, b) => {
        const left = priority[(a[1].status ?? "").toLowerCase()] ?? 4;
        const right = priority[(b[1].status ?? "").toLowerCase()] ?? 4;
        if (left !== right) return left - right;
        return a[0].localeCompare(b[0]);
      });
  }, [data?.accounts_progress]);

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Typography component="h1" variant="h4" sx={{ flexGrow: 1 }}>
          Dashboard
        </Typography>
        {canManageSettings ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              label="Data da archiviare"
              type="date"
              size="small"
              value={runDate}
              onChange={(event) => setRunDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowRoundedIcon />}
              disabled={jobInProgress || runNow.isPending}
              onClick={() =>
                runNow.mutateAsync(
                  runDate ? { date: runDate } : {},
                  {
                    onSuccess: () => notify({ message: "Esecuzione manuale avviata.", severity: "success" }),
                    onError: (err) =>
                      notify({ message: `Esecuzione fallita: ${extractApiError(err)}`, severity: "error" })
                  }
                )
              }
            >
              {runDate ? `Avvia ${runDate}` : "Avvia ora"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={jobInProgress || runCatchUp.isPending}
              onClick={() =>
                runCatchUp.mutateAsync(undefined, {
                  onSuccess: (result) => {
                    if (!result.accepted) {
                      notify({ message: result.message, severity: "info" });
                      return;
                    }
                    notify({
                      message: `Catch-up avviato: ${result.start_date} -> ${result.end_date} (${result.missing_days} giorni).`,
                      severity: "success"
                    });
                  },
                  onError: (err) =>
                    notify({ message: `Catch-up fallito: ${extractApiError(err)}`, severity: "error" })
                })
              }
            >
              Catch-up pausa
            </Button>
            <Button
              variant="outlined"
              startIcon={schedulerRunning ? <PauseRoundedIcon /> : <RefreshRoundedIcon />}
              disabled={toggleScheduler.isPending || jobInProgress}
              onClick={() =>
                toggleScheduler.mutateAsync(schedulerRunning, {
                  onSuccess: () =>
                    notify({
                      message: schedulerRunning ? "Scheduler messo in pausa." : "Scheduler riattivato.",
                      severity: "success"
                    }),
                  onError: (err) =>
                    notify({
                      message: `Aggiornamento scheduler fallito: ${extractApiError(err)}`,
                      severity: "error"
                    })
                })
              }
            >
              {schedulerRunning ? "Metti in pausa" : "Riattiva"}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Grid container spacing={2} columns={{ xs: 1, sm: 6, lg: 12 }}>
        <Grid size={{ xs: 1, sm: 3, lg: 3 }}>
          <Card>
            <CardHeader title="Stato" subheader={isLoading ? "Caricamento..." : undefined} />
            <CardContent>
              {isError ? (
                <Typography variant="body2" color="error">
                  {extractApiError(error)}
                </Typography>
              ) : (
                <>
                  <StatusChip status={jobInProgress ? "running" : schedulerRunning ? "active" : "paused"} label={statusLabel} />
                  <Chip
                    size="small"
                    color={sourceSyncEnabled ? "info" : "default"}
                    label={schedulerModeLabel}
                    sx={{ ml: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Ultima esecuzione:
                  </Typography>
                  <Typography variant="body1">{lastRun}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Prossima finestra:
                  </Typography>
                  <Typography variant="body1">{nextRun}</Typography>
                  {sourceSyncEnabled ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Prossimo source-sync ({sourceSyncIntervalHours === 1 ? "ogni ora" : `ogni ${sourceSyncIntervalHours} ore`}):
                      </Typography>
                      <Typography variant="body1">{nextSourceSync}</Typography>
                    </>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, sm: 3, lg: 3 }}>
          <Card>
            <CardHeader title="Archivio" />
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Base path:
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                {basePath}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Caselle configurate:
              </Typography>
              <Typography variant="h5">{data?.accounts_total ?? "—"}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, sm: 6, lg: 6 }}>
          <Card>
            <CardHeader
              title="Avanzamento caselle"
              subheader={jobInProgress ? "Esecuzione in corso" : "Ultimo stato registrato"}
            />
            <CardContent sx={{ pt: 0 }}>
              {progressEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Nessun progresso disponibile.
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {progressEntries.map(([account, snapshot]) => {
                    const percent = typeof snapshot.progress === "number"
                      ? Math.round(snapshot.progress * 100)
                      : null;
                    const stageLabel = formatStageLabel(snapshot.stage);
                    const durationLabel = formatDuration(snapshot.duration_seconds);
                    const displayName = accountLabels[account] ?? account;
                    return (
                      <ListItem key={account} alignItems="flex-start" disableGutters sx={{ py: 1 }}>
                        <ListItemText
                          primary={
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                              <Stack>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {displayName}
                                </Typography>
                                {accountLabels[account] ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {account}
                                  </Typography>
                                ) : null}
                              </Stack>
                              <Chip
                                size="small"
                                color={accountStatusColor(snapshot.status)}
                                label={accountStatusLabel(snapshot.status)}
                              />
                              {snapshot.updated_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Aggiornato: {formatDate(snapshot.updated_at)}
                                </Typography>
                              )}
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <LinearProgress
                                variant={percent !== null ? "determinate" : "indeterminate"}
                                value={percent ?? undefined}
                                sx={{ height: 6, borderRadius: 1 }}
                              />
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                                {percent !== null && (
                                  <Typography variant="caption" color="text.secondary">
                                    {percent}% completato
                                  </Typography>
                                )}
                                {stageLabel && (
                                  <Typography variant="caption" color="text.secondary">
                                    Fase: {stageLabel}
                                  </Typography>
                                )}
                                {snapshot.total_messages != null && (
                                  <Typography variant="caption" color="text.secondary">
                                    Messaggi: {snapshot.total_messages}
                                  </Typography>
                                )}
                                {durationLabel && (
                                  <Typography variant="caption" color="text.secondary">
                                    Durata: {durationLabel}
                                  </Typography>
                                )}
                              </Stack>
                              {snapshot.archive_filename && (
                                <Typography variant="caption" color="text.secondary">
                                  Archivio: {snapshot.archive_filename}
                                </Typography>
                              )}
                              {snapshot.error && (
                                <Typography variant="caption" sx={{ color: "error.main" }}>
                                  Errore: {snapshot.error}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, sm: 6, lg: 12 }}>
          <Card>
            <CardHeader title="Dettagli ultima esecuzione" action={isRefetching && <RefreshRoundedIcon className="animate-spin" />} />
            <CardContent>
              {data?.last_run_report ? (
                <Box component="pre" sx={{ fontSize: 13, maxHeight: 260, overflow: "auto", p: 2, borderRadius: 2, bgcolor: "background.default" }}>
                  {JSON.stringify(data.last_run_report, null, 2)}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuna esecuzione recente.
                </Typography>
              )}
              {data?.last_run_error && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  Errore: {data.last_run_error}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {canManageSettings ? (
          <Grid size={{ xs: 1, sm: 6, lg: 12 }}>
            <Card>
              <CardHeader title="Esecuzione per casella" />
              <CardContent>
                {accountsQuery.isLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Caricamento caselle...
                  </Typography>
                ) : accountsQuery.isError ? (
                  <Typography variant="body2" color="error">
                    {extractApiError(accountsQuery.error)}
                  </Typography>
                ) : accountsList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessuna casella configurata.
                  </Typography>
                ) : (
                  <List dense>
                    {accountsList.map((acc) => {
                      const dateValue = accountDates[acc.name] ?? "";
                      const label = acc.label || accountLabels[acc.name] || acc.name;
                      return (
                        <ListItem key={acc.name} divider disableGutters>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ width: "100%" }}>
                            <Stack sx={{ flexGrow: 1, minWidth: 200 }}>
                              <Typography variant="subtitle1">{label}</Typography>
                              {label !== acc.name && (
                                <Typography variant="caption" color="text.secondary">
                                  {acc.name}
                                </Typography>
                              )}
                            </Stack>
                            <TextField
                              size="small"
                              type="date"
                              label="Data"
                              value={dateValue}
                              onChange={(e) =>
                                setAccountDates((prev) => ({
                                  ...prev,
                                  [acc.name]: e.target.value
                                }))
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PlayArrowRoundedIcon />}
                                disabled={jobInProgress || runAccount.isPending}
                                onClick={() =>
                                  runAccount.mutate(
                                    dateValue ? { account: acc.name, date: dateValue } : { account: acc.name },
                                    {
                                      onSuccess: () =>
                                        notify({ message: `Avvio per ${acc.name} eseguito.`, severity: "success" }),
                                      onError: (err) =>
                                        notify({
                                          message: `Avvio fallito: ${extractApiError(err)}`,
                                          severity: "error"
                                        })
                                    }
                                  )
                                }
                              >
                                Avvia
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={jobInProgress || runAccount.isPending}
                                onClick={() =>
                                  runAccount.mutate(
                                    { account: acc.name, full_history: true },
                                    {
                                      onSuccess: () =>
                                        notify({
                                          message: `Full sync per ${acc.name} avviato.`,
                                          severity: "success"
                                        }),
                                      onError: (err) =>
                                        notify({
                                          message: `Full sync fallito: ${extractApiError(err)}`,
                                          severity: "error"
                                        })
                                    }
                                  )
                                }
                              >
                                Full sync
                              </Button>
                            </Stack>
                          </Stack>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        ) : null}
      </Grid>
    </Stack>
  );
}
